import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import SharedForm from "@widgets/SharedForm";
import { showDialog } from "@widgets/CommonDialog";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnResizable from "@shared/components/SnResizable";
import SnButton from "@shared/components/SnButton";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import { UseApiService } from "@shared/hooks/useApiService";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getAutoSlip, getInitFormData } from "@shared/utils/CommonUtils";
import { parseJsonValue, parseJsonObject } from "@shared/utils/ValueConverterUtils";
import { global, chatCopyProgressStore, withMenuLoading } from "@shared/libs/Global";
import { menuInfo } from "@shared/type/CommonType";
import { dateToString } from "@shared/utils/DateUtils";
import {
  clearMeetingAudio,
  getMeetingAudio,
  pendingMeetingAudioKeys,
  requestTranscriptView,
  setMeetingAudio,
} from "@shared/libs/meetingAudioStore";
import { v4 as uuidv4 } from "uuid";
import {
  MEETING_MINUTES_URL,
  MEETING_PROGRESS_URL,
  MEETING_TRANSCRIBE_URL,
  UploadedAudio,
  fnUploadMeetingAudio,
} from "./SN_SFA_MEETING_REG_001_api";

interface Props {
  Menuprops: menuInfo;
}

/**
 * 목적: AI 회의록 작성 화면 — 왼쪽 회의록 목록, 오른쪽 상세(녹음 · 전사 원문 · 회의록)와 첨부.
 *
 * 짜임은 기존 목록 + 상세 화면(SN_PRD_DIE_REG_001 · SN_PUR_BAS_REG_001)을 따른다(2026-10-22 다시 씀).
 * - 상세는 그리드가 넘겨 준 행 그대로(focusedRowData)다. 따로 사본을 두지 않는다 — 사본이 낡아 고친 값이 되돌아갔다.
 * - 프로그램이 값을 넣을 때는 그리드의 지금 행(focusedRowNode)을 기준으로 바꾸고 폼에도 다시 넘긴다(patchRow).
 * - AI 처리 중에는 공통 처리 중 표시(withMenuLoading)로 탭 전체를 덮는다 — 툴바도 막힌다.
 * - 삭제는 확인 후 다른 수정과 함께 저장하고, 저장 뒤 다시 조회해 편집하던 회의록을 다시 고른다.
 * - 녹음 · 선택한 음성은 행마다 보관소(meetingAudioStore)에 두었다가 전사 · 저장 때 한 번 올린다.
 *   전사는 올린 음성의 경로(dc_url)로 부른다. 전사 · 회의록 생성은 BE 가 AI 중앙에 맡긴다.
 * - 전사 진행은 탭 가운데 진행 창(공통, 챗봇 행복사와 같은 창)에 — 올리는 비율, 나누는 중, 받아쓴 덩어리 수(1초마다 PROGRESS_01 로 묻는다).
 *   회의록 생성은 AI 호출 한 번이라 같은 창에 막대만 흐르고 걸린 시간(초)을 센다.
 */
const SN_SFA_MEETING_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();
  const { apiPost } = UseApiService();
  const searchRef = useRef<any>(null);
  const gridRef = useRef<any>(null);
  const detailFormRef = useRef<any>(null);
  // 다시 조회한 뒤 고를 회의록번호
  const pendingSelectedNoRef = useRef<string | null>(null);
  const cd_menu = Menuprops.cd_menu || "SN_SFA_MEETING_REG_001";
  const defaultSize = { panel1width: "36%", panel2width: "64%" };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);

  const [searchData, setSearchData] = useState<any>();
  const [searchControlData, setSearchControlData] = useState<any[]>([]);
  const [detailControlData, setDetailControlData] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [focusedRowData, setFocusedRowData] = useState<any>(null);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onSave,
    onDelete,
  }));

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const rtn: any = await getInitFormData({});
        setSearchData(rtn.formData);
        setSearchControlData(rtn.formControlData || []);
        setDetailControlData(rtn.form?.form1 || []);
        setColumns(rtn.grid?.grid1 || []);
      } catch (error) {}
    };

    fetchForm();
    // 화면을 닫으면 올리지 않은 음성을 버린다
    return () => clearMeetingAudio();
  }, []);

  //#region 조회
  const reloadMeetings = async (preferredNo?: string | null) => {
    const searchForm = searchRef.current?.getFormdata?.() || searchData || {};
    const data: any = await fnGetDataSet({
      id: "sn_sfa_meeting_reg_001_01",
      params: {
        ...searchForm,
        // sys_formctrl cd_param은 화면 표준명을 유지하고, 조회 SQL 파라미터명은 기존 query alias에 맞춘다.
        search_type: searchForm.fg_search,
        search_text: searchForm.dc_search,
      },
    });
    const rows = data?.[0] || [];

    pendingSelectedNoRef.current = preferredNo || null;
    // 다시 조회하면 행 키가 새로 생긴다. 올리지 않은 음성은 조회 전에 저장 여부를 물었다
    clearMeetingAudio();
    gridRef.current?.resetUpdate();
    setDataSource(rows);
    if (rows.length === 0) setFocusedRowData(null);
  };

  // 조회 결과가 그리드에 들어간 뒤 — 편집하던 회의록을 다시 고른다(없으면 그리드가 고른 첫 행 그대로)
  const onMeetingRowsReady = useCallback(() => {
    const targetNo = pendingSelectedNoRef.current;
    const grid = gridRef.current;
    pendingSelectedNoRef.current = null;
    if (!targetNo || !grid) return;

    for (let index = 0; index < grid.getDisplayedRowCount(); index++) {
      const rowNode = grid.getRowNodeAtIndex(index);
      if (String(rowNode?.data?.no_meeting) === String(targetNo)) {
        grid.selectAndFocusRow(index);
        return;
      }
    }
  }, []);

  const currentRowNode = () => gridRef.current?.focusedRowNode?.() || null;

  // 저장하지 않은 것 — 그리드 변경분 또는 아직 올리지 않은 음성
  const hasUnsaved = () => {
    const changes = gridRef.current?.getChange?.();
    return (changes?.modifiedRowcount ?? 0) > 0 || pendingMeetingAudioKeys().length > 0;
  };

  const onSearch = async () => {
    if (hasUnsaved()) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      // 저장하면 저장이 다시 조회까지 한다(두 번 조회하지 않는다)
      if (confirm) return Boolean(await onSave());
      if (confirm === undefined) return false;
    }

    await reloadMeetings(currentRowNode()?.data?.no_meeting);
    return true;
  };
  //#endregion

  //#region 신규
  const onInsert = () => {
    gridRef.current?.addRow?.();
  };

  const onBeforeAddRow = () => ({
    cd_firm: userInfo.cd_firm,
    no_meeting: "",
    dt_meeting: dateToString(new Date()),
    nm_meeting: "",
    fg_meeting: "IN",
    nm_cust: "",
    cd_emp: userInfo.cd_emp || "",
    nm_emp: userInfo.nm_emp || "",
    js_participant: [],
    js_tag: [],
    js_attach: [],
    dc_stt: "",
    js_minutes: {},
  });
  //#endregion

  //#region 삭제
  const onDelete = async () => {
    const grid = gridRef.current;
    if (!grid) return;

    const confirm = await showDialog("confirm", "선택한 회의록을 삭제하시겠습니까?");
    if (!confirm) return;

    const key = grid.focusedRowNode?.()?.data?.KeyField;
    if (key) setMeetingAudio(String(key), null);
    grid.deleteSelectedRows();
    if (grid.getDisplayedRowCount() === 0) setFocusedRowData(null);

    // 저장 전(신규) 행만 지웠으면 서버에 보낼 것이 없다. 다른 행의 변경 추적은 그대로 둔다
    const realDeletedRows = grid.getChange().delete ?? [];
    if (!Array.isArray(realDeletedRows) || realDeletedRows.length < 1) return;

    return await onSave();
  };
  //#endregion

  //#region 음성 올리기
  const findAudioAttach = (jsAttach: any) =>
    parseJsonValue(jsAttach).find(
      (item: any) => item?.tp_file === "AUDIO" && item?.dc_url
    ) || null;

  // js_attach 의 AUDIO 항목을 올린 음성으로 바꾼다. 다른 항목은 그대로 둔다
  const withAudioAttach = (jsAttach: any, audio: UploadedAudio) => {
    const list = parseJsonValue(jsAttach).filter(
      (item: any) => item?.tp_file !== "AUDIO"
    );
    list.push({ tp_file: "AUDIO", nm_file: audio.nm_file, dc_url: audio.dc_url });
    return JSON.stringify(list);
  };

  /**
   * 그리드 행에 값을 넣는다. 기준은 그리드에 있는 지금 값이다(폼에서 고친 값이 다 들어 있다).
   * 지금 보는 행이면 폼에도 다시 넘긴다 — 그리드만 바꾸면 폼이 다시 그려지지 않는다.
   */
  const patchRow = (rowNode: any, patch: Record<string, any>) => {
    const merged = { ...rowNode.data, ...patch };
    gridRef.current?.sharedCellValueChanged?.(merged);
    if (currentRowNode()?.data?.KeyField === merged.KeyField) {
      setFocusedRowData(merged);
    }
    return merged;
  };

  /** 이 행의 올리지 않은 음성을 올리고 js_attach 에 경로를 쓴다. 올릴 것이 없으면 행 그대로, 실패하면 null */
  const uploadPendingAudio = async (
    rowNode: any,
    onProgress?: (percent: number) => void
  ): Promise<any | null> => {
    const key = String(rowNode?.data?.KeyField || "");
    const audio = getMeetingAudio(key);
    if (!audio?.file) return rowNode.data;

    try {
      const uploaded = await fnUploadMeetingAudio(
        audio.file,
        `${cd_menu}/${rowNode.data.no_meeting || "NEW"}`,
        onProgress
      );
      if (!uploaded) throw new Error("upload failed");
      setMeetingAudio(key, null);
      return patchRow(rowNode, {
        js_attach: withAudioAttach(rowNode.data.js_attach, uploaded),
      });
    } catch (error) {
      await showDialog("error", "음성 파일 업로드에 실패했습니다.");
      return null;
    }
  };

  /** 올리지 않은 음성을 모두 올린다. 하나라도 실패하면 false — 저장하지 않는다(저장된 음성 연결을 잃지 않게) */
  const uploadAllPendingAudio = async () => {
    for (const key of pendingMeetingAudioKeys()) {
      const rowNode = gridRef.current?.getRowNode?.(key);
      if (!rowNode) {
        // 지운 행
        setMeetingAudio(key, null);
        continue;
      }
      if (!(await uploadPendingAudio(rowNode))) return false;
    }
    return true;
  };
  //#endregion

  //#region 저장
  const onSave = async (reloadAfterSave = true) => {
    const grid = gridRef.current;
    if (!grid) return false;

    // 필수값 검사는 공통 그리드 기능을 사용한다(그리드 칼럼 yn_required).
    if (!grid.requiredValuesCheck()) return false;
    if (!(await uploadAllPendingAudio())) return false;

    const changes = grid.getChange();
    if (!changes || changes.modifiedRowcount === 0) {
      await showDialog("alter", "저장할 내용이 없습니다.");
      return false;
    }

    const focused = grid.focusedRowNode?.()?.data;
    // 신규 행은 저장할 때 번호가 생긴다. 다시 조회한 뒤 편집하던 회의록을 고르려고 행 키 → 번호를 남긴다
    const noByKey: Record<string, string> = {};
    const sfa_meetingh = await normalizeMeetingChanges(changes, noByKey);

    const ok = await fnSave("SN_SFA_MEETING_REG_001_SAVE_01", { sfa_meetingh });
    if (!ok) return false;

    grid.resetUpdate();
    if (reloadAfterSave) {
      await reloadMeetings(
        (focused?.KeyField && noByKey[focused.KeyField]) || focused?.no_meeting
      );
    }
    return true;
  };

  /**
   * Purpose: keep the SnGrid.getChange() shape and normalize only insert/update rows.
   * Called from: onSave().
   */
  const normalizeMeetingChanges = async (changes: any, noByKey: Record<string, string>) => ({
    ...changes,
    insert: await Promise.all(
      (changes.insert || []).map((row: any) => normalizeMeetingRow(row, noByKey))
    ),
    update: await Promise.all(
      (changes.update || []).map((row: any) => normalizeMeetingRow(row, noByKey))
    ),
    delete: (changes.delete || []).map((row: any) => ({
      cd_firm: row.cd_firm || userInfo.cd_firm,
      no_meeting: row.no_meeting,
    })),
  });

  /**
   * Purpose: prepare a meeting row for BE save without changing the grid change contract.
   * Called from: normalizeMeetingChanges().
   */
  const normalizeMeetingRow = async (row: any, noByKey: Record<string, string>) => {
    const noMeeting =
      row.no_meeting || (await getAutoSlip("SFA", 5, row.dt_meeting, cd_menu));
    if (row.KeyField) noByKey[row.KeyField] = noMeeting;
    const jsMinutes = parseJsonObject(row.js_minutes);

    const normalized: any = {
      ...row,
      cd_firm: row.cd_firm || userInfo.cd_firm,
      no_meeting: noMeeting,
      js_participant: parseJsonValue(row.js_participant),
      js_tag: parseJsonValue(row.js_tag),
      js_attach: parseJsonValue(row.js_attach),
    };

    delete normalized.KeyField;
    delete normalized.state;
    delete normalized.block;
    delete normalized.fixCheckbox;

    if (jsMinutes) {
      normalized.js_minutes = jsMinutes;
    }

    return normalized;
  };
  //#endregion

  const onFocusedRowChanged = useCallback((row: any | null) => {
    setFocusedRowData(row);
  }, []);

  //#region AI 전사 · 회의록 생성
  // 회의록 에디터(SnEditor) 컨트롤의 본문 key를 form1 메타에서 찾는다(없으면 기본값).
  const getMinutesValueKey = () => {
    const editorControl = detailControlData.find(
      (control) => control.tp_ctrl === "SnEditor"
    );
    return editorControl?.cd_displaycolumn || "txt_minutes";
  };

  // 회의록 본문의 글자(태그 · 빈칸 제외). 있으면 덮기 전에 묻는다
  const minutesText = (row: any) => {
    const minutes = parseJsonObject(row?.js_minutes);
    const html = String(minutes?.[getMinutesValueKey()] ?? minutes?.text ?? "");
    return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  };

  /**
   * 전사 진행 창 — 탭 가운데 막대 · 퍼센트 · 문구(공통 진행 창, 챗봇 행복사와 같은 창).
   * total 을 모르는 단계(준비 · 나누기)는 막대만 흐른다
   */
  const showTranscribeProgress = (message: string, done = 0, total = 0) =>
    chatCopyProgressStore
      .getState()
      .setProgress(cd_menu, done, total, { title: "AI 전사", message });

  /**
   * 전사가 도는 동안 1초마다 진행을 물어 진행 창을 바꾼다. 돌려준 함수를 부르면 멈춘다.
   * 묻기가 실패해도 오류창을 띄우지 않는다(콜백을 넘기면 apiPost 가 창을 띄우지 않는다) — 진행 표시는 덤이다.
   */
  const watchTranscribeProgress = (idRequest: string) => {
    let stopped = false;
    let timer: number | undefined;

    const tick = async () => {
      const response = await apiPost(MEETING_PROGRESS_URL, { id_request: idRequest }, () => {});
      if (stopped) return;
      const progress = response.status === "S" ? response.data : null;
      if (progress?.stage === "TRANSCRIBE") {
        const total = Number(progress.total) || 0;
        const done = Number(progress.done) || 0;
        showTranscribeProgress(
          total > 0 ? `받아쓰고 있습니다 ${done} / ${total}` : "받아쓰고 있습니다",
          done,
          total
        );
      } else if (progress?.stage === "SPLIT") {
        showTranscribeProgress("음성을 나누고 있습니다");
      }
      timer = window.setTimeout(tick, 1000);
    };

    timer = window.setTimeout(tick, 1000);
    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
    };
  };

  /**
   * 목적: 녹음/업로드된 음성으로 전사 원문(dc_stt)을 채운다.
   * 호출: 상세정보 헤더 "전사" 버튼.
   */
  const onTranscribe = async () => {
    const rowNode = currentRowNode();
    const row = rowNode?.data;
    if (!row) return;

    const hasPending = Boolean(getMeetingAudio(String(row.KeyField || ""))?.file);
    if (!hasPending && !findAudioAttach(row.js_attach)) {
      await showDialog("alter", "먼저 음성을 녹음하거나 파일을 올려 주세요.");
      return;
    }
    if (String(row.dc_stt || "").trim()) {
      const confirm = await showDialog(
        "confirm",
        "전사 원문이 이미 있습니다. 새로 전사한 내용으로 바꾸시겠습니까?"
      );
      if (!confirm) return;
    }

    // 탭은 처리 중 표시로 막고(툴바도), 그 위에 진행 창을 띄운다
    await withMenuLoading(cd_menu, async () => {
      showTranscribeProgress("음성을 준비하고 있습니다");
      let response: any;
      try {
        // 올리지 않은 음성은 먼저 올린다 — 저장할 때 다시 올리지 않는다
        const uploadedRow = hasPending
          ? await uploadPendingAudio(rowNode, (percent) =>
              showTranscribeProgress("음성을 올리고 있습니다", percent, 100)
            )
          : row;
        const audio = uploadedRow ? findAudioAttach(uploadedRow.js_attach) : null;
        if (!audio) return;

        // 진행을 같은 요청 ID 로 묻는다
        const idRequest = uuidv4();
        const body = new FormData();
        body.append("dc_url", audio.dc_url);
        body.append("language", "ko");
        body.append("id_request", idRequest);
        // 받아쓰기 참고 글 — 이름 · 용어를 맞게 쓰게 한다(고객사 용어는 서버가 더한다)
        body.append("nm_meeting", String(uploadedRow.nm_meeting ?? ""));
        body.append("nm_cust", String(uploadedRow.nm_cust ?? ""));
        body.append("js_participant", JSON.stringify(parseJsonValue(uploadedRow.js_participant)));
        body.append("js_tag", JSON.stringify(parseJsonValue(uploadedRow.js_tag)));
        showTranscribeProgress("음성을 나누고 있습니다");
        const stopWatching = watchTranscribeProgress(idRequest);
        try {
          response = await apiPost(MEETING_TRANSCRIBE_URL, body);
        } finally {
          stopWatching();
        }
      } finally {
        chatCopyProgressStore.getState().clearProgress(cd_menu);
      }
      // 실패하면 공통 오류창이 서버 문구로 이미 떴다
      if (response?.status !== "S") return;

      const text = String(response.data?.dc_stt || "").trim();
      if (!text) {
        await showDialog("alter", "전사 결과가 비어 있습니다.");
        return;
      }
      const latest = currentRowNode();
      if (latest) {
        const merged = patchRow(latest, { dc_stt: text });
        // 전사 원문 칸이 편집 중이었어도 새 전사는 보기(시각을 누르면 재생)로 보인다
        requestTranscriptView(String(merged.KeyField || ""));
      }
      // 누락 확인 — 다시 받아써도 글이 짧거나 같은 글자를 되풀이한 구간(전사 원문에 주황색 "※" 줄)
      const checkCount = Number(response.data?.qt_check) || 0;
      if (checkCount > 0) {
        await showDialog(
          "alter",
          `받아쓴 글을 확인해야 할 구간이 ${checkCount}곳 있습니다. 전사 원문의 주황색 표시를 확인해 주세요.`
        );
      }
    });
  };

  /**
   * 목적: 전사 원문으로 회의록 초안을 생성해 에디터(js_minutes)와 검색용 텍스트(dc_minutes)에 반영한다.
   * 호출: 상세정보 헤더 "AI 회의록 생성" 버튼.
   */
  const onGenerateMinutes = async () => {
    const row = currentRowNode()?.data;
    if (!row) return;

    const transcript = String(row.dc_stt || "").trim();
    if (!transcript) {
      await showDialog("alter", "먼저 전사 원문을 만들거나 입력해 주세요.");
      return;
    }
    if (minutesText(row)) {
      const confirm = await showDialog(
        "confirm",
        "회의록이 이미 있습니다. 새로 만든 회의록으로 바꾸시겠습니까?"
      );
      if (!confirm) return;
    }

    // 탭은 처리 중 표시로 막고(툴바도), 그 위에 진행 창 — AI 호출 한 번이라 몇 % 인지 알 수 없어 막대만 흐르고 걸린 시간을 센다
    await withMenuLoading(cd_menu, async () => {
      const progress = chatCopyProgressStore.getState();
      const startedAt = Date.now();
      const showElapsed = () =>
        progress.setProgress(cd_menu, 0, 0, {
          title: "AI 회의록",
          message: `AI 회의록을 만들고 있습니다 · ${Math.floor((Date.now() - startedAt) / 1000)}초`,
        });
      showElapsed();
      const timer = window.setInterval(showElapsed, 1000);
      let response: any;
      try {
        response = await apiPost(MEETING_MINUTES_URL, {
          dc_stt: transcript,
          nm_meeting: row.nm_meeting,
          dt_meeting: row.dt_meeting,
          fg_meeting: row.fg_meeting,
          nm_cust: row.nm_cust,
          js_participant: parseJsonValue(row.js_participant),
        });
      } finally {
        window.clearInterval(timer);
        progress.clearProgress(cd_menu);
      }
      if (response?.status !== "S") return;

      const minutes = String(response.data?.dc_minutes || "").trim();
      if (!minutes) {
        await showDialog("alter", "회의록 생성 결과가 비어 있습니다.");
        return;
      }
      const latest = currentRowNode();
      if (!latest) return;
      // js_minutes는 에디터가 쓰는 모양 그대로, dc_minutes는 검색용으로 반영한다.
      const valueKey = getMinutesValueKey();
      patchRow(latest, {
        js_minutes: JSON.stringify({ [valueKey]: minutes, text: minutes }),
        dc_minutes: minutes,
      });
    });
  };
  //#endregion

  return (
    <div className="h-full flex flex-col overflow-hidden relative">
      <style>
        {`
          .sn-sfa-meeting-detail .MuiFormGroup-row {
            flex-wrap: nowrap;
          }

          .sn-sfa-meeting-detail .MuiFormControlLabel-root {
            white-space: nowrap;
          }

          /* 라벨칸을 좁혀 라벨-컨트롤 간격을 줄인다(SnLabel 의 SharedForm 기본 130px 인라인 폭 override). */
          .sn-sfa-meeting-detail .flex.justify-end > .flex.items-center.h-full {
            min-width: 90px !important;
            max-width: 90px !important;
          }
        `}
      </style>
      <div className="mb-1" style={{ flexShrink: 0 }}>
        <SearchPanel
          ref={searchRef}
          data={searchData}
          controls={searchControlData}
        />
      </div>

      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={220}
        >
          <SnGrid
            ref={gridRef}
            columnConfig={columns}
            rowData={dataSource}
            etcProps={{
              isEditable: false,
              rowSelection: "single",
              title: "회의록 목록",
            }}
            events={{
              onFocusedRowChanged: onFocusedRowChanged,
              onBeforeAddRow: onBeforeAddRow,
              onRowDataReady: onMeetingRowsReady,
            }}
          />
        </SnResizable>

        <SnResizable
          direction={"width"}
          seq={2}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={360}
        >
          <div className="h-full flex flex-col min-h-0 overflow-hidden">
            <div className="sn-sfa-meeting-detail flex-1 min-h-0 overflow-hidden">
              <SharedForm
                ref={detailFormRef}
                data={focusedRowData}
                controls={detailControlData}
                gridref={gridRef}
                title="상세정보"
                header={
                  <div>
                    <SnButton
                      text="전사"
                      disabled={!focusedRowData}
                      onClick={onTranscribe}
                    />
                    <SnButton
                      text="AI 회의록 생성"
                      disabled={!focusedRowData}
                      onClick={onGenerateMinutes}
                    />
                  </div>
                }
              />
            </div>
            <div
              style={{
                flexShrink: 0,
                borderTop: "1px solid #e0e0e0",
                padding: "6px 8px",
                maxHeight: 170,
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 4,
                  color: "#333",
                }}
              >
                첨부파일
              </div>
              {/* 첨부는 선택 행의 회의록번호로 묶는다. 신규(미저장)면 "" → 조회 안 함 */}
              <SnFileUploadBox
                selectedDocu={String(focusedRowData?.no_meeting || "")}
                selectedLineDocu={0}
              />
            </div>
          </div>
        </SnResizable>
      </div>
    </div>
  );
});

export default SN_SFA_MEETING_REG_001;
